import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchOfferings,
  restorePurchases,
  purchasePackage,
  selectIsPremium,
  selectOfferings,
  selectCurrentOffering,
  selectPremiumFeatures,
  selectActiveSubscriptions,
  selectHasFeature,
  fetchPeakInsights,
  selectPeakInsights,
  redeemPromoCode} from '../store/slices/premiumSlice';
// Import status selectors from the simple slice
import {
  selectPurchaseStatus,
  selectRestoreStatus
} from '../store/slices/premiumSliceSimple';
import { selectUser } from '../store/selectors';
import { PremiumFeature } from '../types/premium';

/**
 * Premium features hook - centralizes premium subscription functionality
 */
const usePremium = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const user = useAppSelector(selectUser);
  const isPremium = useAppSelector(selectIsPremium);
  const offerings = useAppSelector(selectOfferings);
  const currentOffering = useAppSelector(selectCurrentOffering);
  const features = useAppSelector(selectPremiumFeatures);
  const activeSubscriptions = useAppSelector(selectActiveSubscriptions);
  const purchaseStatus = useAppSelector(selectPurchaseStatus);
  const restoreStatus = useAppSelector(selectRestoreStatus);
  const insights = useAppSelector(selectPeakInsights);

  // DISABLED: IAP initialization now happens in FirebaseProvider
  // This prevents duplicate RevenueCat configurations
  useEffect(() => {
    // Only fetch offerings if not already loaded
    if (user?.uid && offerings.length === 0) {
      dispatch(fetchOfferings());
    }
  }, [user?.uid, dispatch, offerings.length]);

  // Feature check functions
  const hasInjuryTracking = useAppSelector(selectHasFeature(PremiumFeature.INJURY_TRACKING));
  const hasPhotoGallery = useAppSelector(selectHasFeature(PremiumFeature.PHOTO_GALLERY));
  const hasCustomMetrics = useAppSelector(selectHasFeature(PremiumFeature.CUSTOM_METRICS));
  const isAdFree = useAppSelector(selectHasFeature(PremiumFeature.AD_FREE));
  const hasGoals = useAppSelector(selectHasFeature(PremiumFeature.GOALS));
  const hasWorkoutRoutines = useAppSelector(selectHasFeature(PremiumFeature.WORKOUT_ROUTINES));
  const hasInsights = useAppSelector(selectHasFeature(PremiumFeature.INSIGHTS));
  const hasRoundTracker = useAppSelector(selectHasFeature(PremiumFeature.ROUND_TRACKER));
  const hasMultipleMartialArts = useAppSelector(selectHasFeature(PremiumFeature.MULTIPLE_MARTIAL_ARTS));
  const hasLeaderboards = useAppSelector(selectHasFeature(PremiumFeature.LEADERBOARDS));

  /**
   * Check if user has access to a specific feature
   */
  const hasFeature = useCallback(
    (feature: PremiumFeature) => {
      return features.includes(feature) || isPremium;
    },
    [features, isPremium]
  );

  /**
   * Restore any previous purchases
   */
  const handleRestorePurchases = useCallback(() => {
    dispatch(restorePurchases());
  }, [dispatch]);

  /**
   * Make a new purchase
   */
  const handlePurchase = useCallback(
    (packageToPurchase: any) => {
      dispatch(purchasePackage(packageToPurchase));
    },
    [dispatch]
  );

  /**
   * Redeem a promo code
   */
  const handleRedeemPromoCode = useCallback(
    (code: string) => {
      dispatch(redeemPromoCode(code));
    },
    [dispatch]
  );

  /**
   * Navigate to the premium landing page
   */
  const goToPremiumLanding = useCallback(() => {
    navigate('/premium');
  }, [navigate]);

  /**
   * Load insights for premium users
   */
  const loadPeakInsights = useCallback(() => {
    if (isPremium) {
      dispatch(fetchPeakInsights());
    }
  }, [dispatch, isPremium]);

  return {
    // Status
    isPremium,
    offerings,
    currentOffering,
    activeSubscriptions,
    purchaseStatus,
    restoreStatus,
    insights,

    // Feature access
    hasFeature,
    hasInjuryTracking,
    hasPhotoGallery,
    hasCustomMetrics,
    isAdFree,
    hasGoals,
    hasWorkoutRoutines,
    hasInsights,
    hasRoundTracker,
    hasMultipleMartialArts,
    hasLeaderboards,

    // Actions
    handleRestorePurchases,
    handlePurchase,
    handleRedeemPromoCode,
    goToPremiumLanding,
    loadPeakInsights,
  };
};

export default usePremium;